import { useMemo } from "react"
import { useTranslation } from "react-i18next"

const ELLIPSIS = "…"

const buildPageRange = (current, total) => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  const pages = new Set([1, total, current, current - 1, current + 1])
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b)

  const result = []
  let prev = null
  for (const page of sorted) {
    if (prev !== null && page - prev > 1) {
      result.push(ELLIPSIS)
    }
    result.push(page)
    prev = page
  }

  return result
}

const Pagination = ({ meta, onPageChange }) => {
  const { t } = useTranslation()

  const { page, total_pages: totalPages, has_next_page: hasNextPage } = meta || {}
  const hasPrevPage = page > 1

  const pageRange = useMemo(() => buildPageRange(page, totalPages), [page, totalPages])

  if (!totalPages || totalPages <= 1) return null

  const goTo = (target) => {
    if (target < 1 || target > totalPages || target === page) return
    onPageChange(target)
  }

  return (
    <div className="flex items-center justify-between gap-3 px-1 py-2">
      <div className="text-xs text-[#9AA5B4]">
        {t("pagination.page_of", { page, total: totalPages })}
      </div>

      <div className="flex items-center gap-1">
        <button
          onClick={() => goTo(page - 1)}
          disabled={!hasPrevPage}
          className="px-3 py-1.5 rounded-xl border border-[#E7EAEF] text-sm font-medium text-[#1F2937]
                     hover:bg-[#F4F6F9] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {t("pagination.prev")}
        </button>

        <div className="hidden sm:flex items-center gap-1">
          {pageRange.map((item, idx) =>
            item === ELLIPSIS ? (
              <span key={`ellipsis-${idx}`} className="w-8 h-8 flex items-center justify-center text-[#9AA5B4] text-sm">
                {ELLIPSIS}
              </span>
            ) : (
              <button
                key={item}
                onClick={() => goTo(item)}
                className={`w-8 h-8 flex items-center justify-center rounded-xl text-sm font-medium transition-colors ${
                  item === page
                    ? "bg-[#1F2937] text-white"
                    : "text-[#1F2937] hover:bg-[#F4F6F9]"
                }`}
              >
                {item}
              </button>
            )
          )}
        </div>

        <button
          onClick={() => goTo(page + 1)}
          disabled={!hasNextPage}
          className="px-3 py-1.5 rounded-xl border border-[#E7EAEF] text-sm font-medium text-[#1F2937]
                     hover:bg-[#F4F6F9] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {t("pagination.next")}
        </button>
      </div>
    </div>
  )
}

export default Pagination
